import * as shared from "@/styles/shared";
import type { StatusIconMap } from "@/types/invoices";
import type { StatusClassMap } from "@shared/types/invoices";
import { STATUS_OPTIONS } from "@shared/types/models";
import type { Invoice, Status } from "@shared/types/models";
import { formatDate } from "@shared/utils";
import { CheckCheck, CheckCircle2, Clock, Loader, SkipForward, Tag, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import * as styles from "./invoiceDialogs.styles";

interface ChangeStatusDialogProps {
  isOpen: boolean;
  invoice: Invoice;
  onStatusChange: (status: Status) => void;
  onClose: () => void;
}

const statusIcons: StatusIconMap = {
  PENDING: <Clock className="w-4 h-4" />,
  PROCESSING: <Loader className="w-4 h-4" />,
  PAID: <CheckCircle2 className="w-4 h-4" />,
  COMPLETED: <CheckCheck className="w-4 h-4" />,
  FAILED: <XCircle className="w-4 h-4" />,
  SKIPPED: <SkipForward className="w-4 h-4" />,
};

const statusIconClasses: StatusClassMap = {
  PENDING: styles.statusPendingIcon,
  PROCESSING: styles.statusProcessingIcon,
  PAID: styles.statusPaidIcon,
  COMPLETED: styles.statusCompletedIcon,
  FAILED: styles.statusFailedIcon,
  SKIPPED: styles.statusSkippedIcon,
};

const statusBorderClasses: StatusClassMap = {
  PENDING: styles.statusPending,
  PROCESSING: styles.statusProcessing,
  PAID: styles.statusPaid,
  COMPLETED: styles.statusCompleted,
  FAILED: styles.statusFailed,
  SKIPPED: styles.statusSkipped,
};

const ChangeStatusDialog = ({ isOpen, invoice, onStatusChange, onClose }: ChangeStatusDialogProps) => {
  const { t } = useTranslation(["common", "invoiceDetail", "status"]);

  if (!isOpen) return null;

  const currentStatus = invoice.status;

  return (
    <div className={styles.dialogOverlay} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <div className={styles.dialogHeader}>
          <div className={styles.dialogTitleWrapper}>
            <Tag className={styles.dialogTitleIcon} />
            <h2 className={styles.dialogTitle}>{t("changeStatusTitle", { ns: "invoiceDetail" })}</h2>
          </div>
          <p className={shared.muted}>{t("clickToChange", { ns: "invoiceDetail", defaultValue: "Click a status to change it" })}</p>
          {invoice.datePaid && <p className={shared.muted}>{t("paidOn", { ns: "invoiceDetail", date: formatDate(invoice.datePaid) })}</p>}
        </div>

        <div className={styles.statusGrid}>
          {STATUS_OPTIONS.map((s) => {
            const isActive = s === currentStatus;
            return (
              <button
                key={s}
                onClick={() => onStatusChange(s)}
                className={`${styles.statusButton} ${statusBorderClasses[s]} ${isActive ? styles.statusButtonActive : ""}`}
              >
                <div className={`${styles.statusIconWrapper} ${statusIconClasses[s]}`}>{statusIcons[s]}</div>
                <span className={styles.statusLabel}>{t(s, { ns: "status" })}</span>
                {isActive && <CheckCircle2 className={`${styles.activeCheck} text-primary`} />}
              </button>
            );
          })}
        </div>

        <button onClick={onClose} className={styles.cancelButton}>
          {t("button.cancel")}
        </button>
      </div>
    </div>
  );
};

export { ChangeStatusDialog };
